import React, { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLang } from '../context/LangContext';
import { GoogleMap, LoadScript, MarkerF } from '@react-google-maps/api';
import wechatImg from "../assets/images/wechat.jpg";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_GOOGLE_MAPS_API_KEY';

// 调试信息（仅在开发环境显示）
if (process.env.NODE_ENV === 'development') {
  console.log('Google Maps API Key Status:', {
    exists: !!process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    length: GOOGLE_MAPS_API_KEY.length,
    startsWith: GOOGLE_MAPS_API_KEY.startsWith('AIza'),
    isDefault: GOOGLE_MAPS_API_KEY === 'YOUR_GOOGLE_MAPS_API_KEY'
  });
}

// 检查 API Key 是否有效
const isApiKeyValid = GOOGLE_MAPS_API_KEY &&
  GOOGLE_MAPS_API_KEY !== 'YOUR_GOOGLE_MAPS_API_KEY' &&
  GOOGLE_MAPS_API_KEY.startsWith('AIza') &&
  GOOGLE_MAPS_API_KEY.length > 30;
const mapContainerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '0.5rem'
};

// 诊所官方精准地理坐标 (1595 16th Ave Suite 103, Richmond Hill, ON L4B 3N9)
const CLINIC_COORDINATES = {
  lat: 43.862782916925745,
  lng: -79.38364294577829
};

const MAP_ZOOM_LEVEL = 16;

const content = {
  en: {
    pageTitle: 'Contact Us',
    metaTitle: 'Contact & Location | Halo Aesthetic Clinic Richmond Hill',
    pageDesc: 'Feel free to contact us at any time. We will provide you with professional service.',
    metaDesc: 'Visit Halo Aesthetic Clinic at 1595 16th Ave Suite 103, Richmond Hill, ON. Call 942-388-1688 or book online for expert medical aesthetic consultations and appointments.',
    addressTitle: 'Address',
    address: '1595 16th Ave Suite 103, Richmond Hill, ON L4B 3N9',
    addressDesc: 'Ample complimentary parking is available',
    contactTitle: 'Contact Info',
    phone: 'Phone: 942-388-1688',
    mobile: 'Mobile: 942-388-1688',
    email: 'Email: info@haloaestheticclinic.com',
    hoursTitle: 'Business Hours',
    businessHours: [
      { day: 'Sunday', hours: 'Closed' },
      { day: 'Monday', hours: '11:00 - 15:00' },
      { day: 'Tuesday', hours: '11:00 - 19:00' },
      { day: 'Wednesday', hours: '11:00 - 19:00' },
      { day: 'Thursday', hours: '11:00 - 19:00' },
      { day: 'Friday', hours: '11:00 - 19:00' },
      { day: 'Saturday', hours: '11:00 - 19:00' },
    ],
    followTitle: 'Follow Us',
    wechatOfficial: 'WeChat Official Account',
    map: 'Map component will be displayed here',
  },
  cn: {
    pageTitle: '联系我们',
    metaTitle: '联系我们与诊所地址 | Halo Richmond Hill 医美诊所',
    pageDesc: '欢迎随时与我们联系，我们将为您提供专业的服务',
    metaDesc: '欢迎联系 Halo Richmond Hill 医美诊所。地址：1595 16th Ave Suite 103, Richmond Hill, ON。电话：942-388-1688，提供营业时间及在线快速预约通道。',
    addressTitle: '地址',
    address: '1595 16th Ave Suite 103, Richmond Hill, ON L4B 3N9',
    addressDesc: '免费停车位充足',
    contactTitle: '联系方式',
    phone: '电话：942-388-1688',
    mobile: '手机：942-388-1688',
    email: '邮箱：info@haloaestheticclinic.com',
    hoursTitle: '营业时间',
    businessHours: [
      { day: '周日', hours: '休息' },
      { day: '周一', hours: '11:00 - 15:00' },
      { day: '周二', hours: '11:00 - 19:00' },
      { day: '周三', hours: '11:00 - 19:00' },
      { day: '周四', hours: '11:00 - 19:00' },
      { day: '周五', hours: '11:00 - 19:00' },
      { day: '周六', hours: '11:00 - 19:00' },
    ],
    followTitle: '关注我们',
    wechatOfficial: '微信公众号',
    map: '地图组件将在这里显示',
  }
};

const Contact = () => {
  const { lang } = useLang();
  const c = content[lang];
  const [mapError, setMapError] = useState(null);

  // 处理地图加载错误
  const handleMapLoadError = useCallback((error) => {
    console.error('Google Maps API Error:', error);
    setMapError(error);
  }, []);

  // 处理地图加载成功
  const handleMapLoad = useCallback(() => {
    setMapError(null);
  }, []);

  const canonicalUrl = 'https://www.haloaestheticclinic.com/contact';

  return (
    <div className="bg-softGradient bg-no-repeat bg-top text-text">
      <Helmet>
        <title>{c.metaTitle}</title>
        <meta name="description" content={c.metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={c.metaTitle} />
        <meta property="og:description" content={c.metaDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        {/* Event snippet for 网页浏览 conversion page
In your html page, add the snippet and call gtag_report_conversion when someone clicks on the chosen link or button. */}
        <script>
          {`function gtag_report_conversion(url) {
  var callback = function () {
    if (typeof(url) != 'undefined') {
      window.location = url;
    }
  };
  gtag('event', 'conversion', {
      'send_to': 'AW-18458381832/6SnuCPThvpcdEIic0uFE',
      'value': 1.0,
      'currency': 'CAD',
      'event_callback': callback
  });
  return false;
}`}
        </script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-section">
        {/* 页面标题 */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-gray-900">{c.pageTitle}</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">{c.pageDesc}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* 联系信息 */}
          <div className="space-y-8">
            {/* 地址 */}
            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-champagne/40">
              <h2 className="text-2xl font-semibold mb-4 text-[#2E6B4C]">{c.addressTitle}</h2>
              <div className="flex items-start space-x-4">
                <svg
                  className="w-6 h-6 text-[#2E6B4C] mt-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <div>
                  <p className="text-gray-700">{c.address}</p>
                  <p className="text-gray-700 mt-1">{c.addressDesc}</p>
                </div>
              </div>
            </div>

            {/* 联系方式 */}
            <div>
              <h2 className="text-2xl font-semibold mb-4">{c.contactTitle}</h2>
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <div>
                    <p className="text-gray-600">
                      <a href="tel:942-388-1688" className="hover:text-green-600 transition-colors">
                        {c.phone}
                      </a>
                    </p>
                    <p className="text-gray-600">
                      <a href="tel:942-388-1688" className="hover:text-green-600 transition-colors">
                        {c.mobile}
                      </a>
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-gray-600">
                    <a href="mailto:info@haloaestheticclinic.com" className="hover:text-green-600 transition-colors">
                      {c.email}
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* 营业时间 */}
            <div>
              <h2 className="text-2xl font-semibold mb-4">{c.hoursTitle}</h2>
              <div className="space-y-2">
                {c.businessHours.map((item, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center"
                  >
                    <span className="text-gray-600">{item.day}</span>
                    <span className="text-gray-600">{item.hours}</span>
                  </div>
                ))}
              </div>
            </div>


            {/* 微信二维码 */}
            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-8 border border-champagne/40 text-center">
              <h2 className="text-2xl font-semibold mb-6 text-[#2E6B4C]">{c.followTitle}</h2>
              <div className="flex justify-center">
                <div className="text-center">
                  <img
                    src={wechatImg}
                    alt="wechat"
                    className="w-40 h-40 mx-auto mb-4 rounded-lg shadow-lg"
                  />
                  <p className="text-gray-700 text-lg font-medium">{c.wechatOfficial}</p>
                  <p className="text-gray-600 text-sm mt-2"></p>
                </div>
              </div>
            </div>
          </div>

          {/* 地图 */}
          <div className="h-[600px] rounded-lg overflow-hidden">
            {isApiKeyValid ? (
              <LoadScript
                googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                libraries={['marker']}
                onLoad={handleMapLoad}
                onError={handleMapLoadError}
                loadingElement={<div className="h-full flex items-center justify-center bg-gray-100 rounded-lg">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E6B4C] mx-auto mb-4"></div>
                    <p className="text-gray-600">{lang === 'cn' ? '地图加载中...' : 'Loading map...'}</p>
                  </div>
                </div>}
              >
                {mapError ? (
                  <div className="h-full flex items-center justify-center bg-gray-100 rounded-lg">
                    <div className="text-center p-8">
                      <div className="text-red-500 mb-4">
                        <svg className="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">
                        {lang === 'cn' ? '地图加载失败' : 'Map Loading Failed'}
                      </h3>
                      <p className="text-sm text-gray-500 mb-2">
                        {lang === 'cn'
                          ? '无法加载 Google 地图。请检查 API Key 配置或网络连接。'
                          : 'Unable to load Google Maps. Please check API Key configuration or network connection.'
                        }
                      </p>
                      <p className="text-xs text-gray-400 mt-4">
                        {mapError.message || 'Unknown error'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={CLINIC_COORDINATES}
                    zoom={MAP_ZOOM_LEVEL}
                    options={{
                      zoomControl: true,
                      streetViewControl: true,
                      mapTypeControl: true,
                      fullscreenControl: true,
                    }}
                  >
                    <MarkerF
                      position={CLINIC_COORDINATES}
                      title="Halo Aesthetic Clinic"
                    />
                  </GoogleMap>
                )}
              </LoadScript>
            ) : (
              <div className="h-full flex items-center justify-center bg-gray-100 rounded-lg">
                <div className="text-center p-8">
                  <div className="text-gray-500 mb-4">
                    <svg className="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    {lang === 'cn' ? '地图配置' : 'Map Configuration'}
                  </h3>
                  <p className="text-sm text-gray-500 mb-2">
                    {lang === 'cn'
                      ? '请确保 Google Maps API Key 已正确配置'
                      : 'Please ensure Google Maps API Key is properly configured'
                    }
                  </p>
                  <div className="text-xs text-gray-400 mt-4 space-y-1">
                    <p>
                      {lang === 'cn'
                        ? '环境变量: REACT_APP_GOOGLE_MAPS_API_KEY'
                        : 'Environment variable: REACT_APP_GOOGLE_MAPS_API_KEY'
                      }
                    </p>
                    <p className="text-red-500 mt-2">
                      {lang === 'cn'
                        ? '提示: 如果已配置但仍显示此提示，请重启开发服务器或检查生产环境变量设置'
                        : 'Tip: If configured but still showing this, restart dev server or check production env vars'
                      }
                    </p>
                    {process.env.NODE_ENV === 'development' && (
                      <p className="text-xs text-gray-500 mt-2 font-mono">
                        Debug: Key exists={!!process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? 'Yes' : 'No'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;